import React from 'react';
import { createPortal } from 'react-dom'
import { Calendar, Clock, MapPin, Navigation, CheckCircle, Timer, Users, X } from 'lucide-react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import API_CONFIG from '../config/api'

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface Waypoint {
  address: string
  type?: 'starting_point' | 'booking'
  customerId?: string
  customerName?: string
  startAt?: string
  phoneNumber?: string
  serviceType?: string
  price?: number
  coordinates: [number, number]
}

interface RouteLeg {
  duration: number;
  distance: number;
}

interface RouteData {
  waypoints: Waypoint[]
  totalDuration: number
  totalDistance: number
  route: {
    geometry?: any;
    legs?: RouteLeg[];
  } | unknown
}

/** Fiche du client dans Recherche Client (historique des rendez-vous, etc.) */
const lienFiche = (customerId: string) => `/client-search?client=${encodeURIComponent(customerId)}`

/** Nom du client ; mène à sa fiche quand on connaît son identifiant */
const NomClient = ({ waypoint, className }: { waypoint: Waypoint; className?: string }) =>
  waypoint.customerId ? (
    <Link
      to={lienFiche(waypoint.customerId)}
      className={`${className || ''} underline decoration-indigo-400/50 underline-offset-2 hover:decoration-cyan-300`}
    >
      {waypoint.customerName || '—'}
    </Link>
  ) : (
    <span className={className}>{waypoint.customerName || '—'}</span>
  )

const CACHE_KEY = (date: string) => `planning_route_${date}`

const getCachedRoute = (date: string): RouteData | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY(date))
    return raw ? (JSON.parse(raw) as RouteData) : null
  } catch {
    return null
  }
}

const setCachedRoute = (date: string, data: RouteData) => {
  try {
    localStorage.setItem(CACHE_KEY(date), JSON.stringify(data))
  } catch {}
}

// Ouvre l'adresse dans Plans (Apple Maps) ; coordinates = [lng, lat]
const appleMapsUrl = (address: string, coordinates?: [number, number]) => {
  const ll = coordinates ? `&ll=${coordinates[1]},${coordinates[0]}` : '';
  return `https://maps.apple.com/?q=${encodeURIComponent(address)}${ll}`;
};

const RouteOptimizerSchedule = () => {
    // /schedule?date=AAAA-MM-JJ : ouverture sur un jour précis, depuis une
    // notification de regroupement possible
    const [searchParams, setSearchParams] = useSearchParams()
    const dateDemandee = searchParams.get('date')
    const dateValide = dateDemandee && /^\d{4}-\d{2}-\d{2}$/.test(dateDemandee) ? dateDemandee : null
    const [date, setDate] = useState<string>(() => dateValide || new Date().toISOString().split('T')[0])
    const [loading, setLoading] = useState<boolean>(false)
    const [refreshing, setRefreshing] = useState<boolean>(false)
    const [routeData, setRouteData] = useState<RouteData | null>(null)
    const [error, setError] = useState<string>('')
    const [shouldFetch, setShouldFetch] = useState<boolean>(false)
    const [currentCardIndex, setCurrentCardIndex] = useState<number>(0)
    const [showSummary, setShowSummary] = useState<boolean>(false)

    const mapRef = useRef<L.Map | null>(null)
    const mapContainerDesktop = useRef<HTMLDivElement>(null)
    const mapContainerMobile = useRef<HTMLDivElement>(null)
    const carouselRef = useRef<HTMLDivElement>(null)
    const scrollRaf = useRef<number>(0)
    const ignoreScroll = useRef<boolean>(false)
    const scrollToIndexRef = useRef<(index: number, smooth?: boolean) => void>(() => {})
    const userLocationMarkerRef = useRef<L.Marker | null>(null)
    const watchIdRef = useRef<number | null>(null)
    const userLocationRef = useRef<{lat: number, lng: number} | null>(null)

    useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            @keyframes plannerMarkerPulse {
                0%, 100% { box-shadow: 0 0 0 4px rgba(99,102,241,0.45), 0 0 16px rgba(99,102,241,1), 0 0 32px rgba(99,102,241,0.65); }
                50%       { box-shadow: 0 0 0 8px rgba(99,102,241,0.2), 0 0 26px rgba(99,102,241,1), 0 0 52px rgba(99,102,241,0.45); }
            }
            @keyframes plannerMarkerPulseStart {
                0%, 100% { box-shadow: 0 0 0 4px rgba(139,92,246,0.45), 0 0 16px rgba(139,92,246,1), 0 0 32px rgba(139,92,246,0.65); }
                50%       { box-shadow: 0 0 0 8px rgba(139,92,246,0.2), 0 0 26px rgba(139,92,246,1), 0 0 52px rgba(139,92,246,0.45); }
            }
            @keyframes userLocPulse {
                0%   { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
                70%  { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
                100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
            }
        `
        document.head.appendChild(style)
        return () => { document.head.removeChild(style) }
    }, [])

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setDate(e.target.value)
      setError('')
      if (e.target.value && window.innerWidth < 1024) setShouldFetch(true)
    }

    useEffect(() => {
        if (shouldFetch && date) {
          fetchOptimizedRoute()
          setShouldFetch(false)
        }
    }, [shouldFetch, date])

    // Jour demandé dans l'adresse, au montage ou page déjà ouverte : on l'affiche,
    // puis on retire le paramètre pour qu'un rechargement revienne à aujourd'hui
    useEffect(() => {
        if (!dateValide) return
        setDate(dateValide)
        setError('')
        setShouldFetch(true)
        const reste = new URLSearchParams(searchParams)
        reste.delete('date')
        setSearchParams(reste, { replace: true })
    }, [dateValide, searchParams, setSearchParams])

    // Auto-fetch today's route on mobile mount — show cache instantly, then refresh in background
    useEffect(() => {
        if (window.innerWidth < 1024 && !dateValide) {
            const cached = getCachedRoute(date)
            if (cached) setRouteData(cached)
            fetchOptimizedRoute()
        }
    }, [])

    // Reset carousel when new route data arrives
    useEffect(() => {
        setCurrentCardIndex(0)
        ignoreScroll.current = true
        setTimeout(() => {
            const el = carouselRef.current
            if (el) el.scrollLeft = 0
            ignoreScroll.current = false
        }, 50)
    }, [routeData])

    const highlightMarker = useCallback((selectedIndex: number) => {
        const waypoints = routeData?.waypoints
        if (!waypoints) return

        waypoints.forEach((_, i) => {
            const el = document.getElementById(`planning-marker-${i}`)
            if (!el) return
            const isSelected = i === selectedIndex
            if (i === 0) {
                el.style.width = isSelected ? '34px' : '22px'
                el.style.height = isSelected ? '34px' : '22px'
                el.style.border = isSelected ? '2.5px solid rgba(255,255,255,1)' : '1px solid rgba(255,255,255,0.8)'
                el.style.boxShadow = isSelected
                    ? '0 0 0 4px rgba(139,92,246,0.4), 0 0 16px rgba(139,92,246,1), 0 0 32px rgba(139,92,246,0.7)'
                    : '0 0 4px rgba(139,92,246,0.5), 0 0 8px rgba(139,92,246,0.3)'
                el.style.animation = isSelected ? 'plannerMarkerPulseStart 1.8s ease-in-out infinite' : 'none'
            } else {
                el.style.width = isSelected ? '34px' : '20px'
                el.style.height = isSelected ? '34px' : '20px'
                el.style.border = isSelected ? '2.5px solid rgba(255,255,255,1)' : '1px solid rgba(255,255,255,0.8)'
                el.style.boxShadow = isSelected
                    ? '0 0 0 4px rgba(99,102,241,0.4), 0 0 16px rgba(99,102,241,1), 0 0 32px rgba(99,102,241,0.7)'
                    : '0 0 4px rgba(99,102,241,0.5), 0 0 8px rgba(99,102,241,0.3)'
                el.style.animation = isSelected ? 'plannerMarkerPulse 1.8s ease-in-out infinite' : 'none'
                el.style.fontSize = isSelected ? '13px' : '11px'
            }
        })
    }, [routeData])

    const scrollToIndex = useCallback((index: number, smooth = true) => {
        const el = carouselRef.current
        if (!el) return
        ignoreScroll.current = true
        el.scrollTo({ left: index * el.clientWidth, behavior: smooth ? 'smooth' : 'auto' })
        setCurrentCardIndex(index)
        highlightMarker(index)
        setTimeout(() => { ignoreScroll.current = false }, smooth ? 320 : 80)
    }, [highlightMarker])

    useEffect(() => {
        scrollToIndexRef.current = scrollToIndex
    }, [scrollToIndex])

    const handleCarouselScroll = () => {
        if (ignoreScroll.current) return
        if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current)
        scrollRaf.current = requestAnimationFrame(() => {
            scrollRaf.current = 0
            const el = carouselRef.current
            if (!el || el.clientWidth === 0) return
            const idx = Math.round(el.scrollLeft / el.clientWidth)
            const total = routeData?.waypoints.length ?? 1
            const clamped = Math.max(0, Math.min(total - 1, idx))
            if (clamped !== currentCardIndex) {
                setCurrentCardIndex(clamped)
                highlightMarker(clamped)
            }
        })
    }

    const [travelTimes, setTravelTimes] = useState<number[]>([]);

    useEffect(() => {
        if (!routeData || !routeData.waypoints || routeData.waypoints.length < 2) {
            setTravelTimes([]);
            return;
        }
        const times: number[] = [];
        for (let i = 0; i < routeData.waypoints.length - 1; i++) {
            let estimatedDuration = 0;
            if (routeData.route && typeof routeData.route === 'object' && 'legs' in routeData.route) {
                const legs = routeData.route.legs;
                if (Array.isArray(legs) && legs[i] && 'duration' in legs[i]) {
                    estimatedDuration = Math.round(legs[i].duration / 60);
                }
            } else {
                const startPoint = routeData.waypoints[i].coordinates;
                const endPoint = routeData.waypoints[i + 1].coordinates;
                const dx = startPoint[1] - endPoint[1];
                const dy = startPoint[0] - endPoint[0];
                const distance = Math.sqrt(dx * dx + dy * dy);
                const totalDist = routeData.totalDistance;
                const ratio = distance / (totalDist * 0.01);
                estimatedDuration = Math.round((routeData.totalDuration * ratio) / 10);
            }
            times.push(estimatedDuration);
        }
        setTravelTimes(times);
    }, [routeData]);

    useEffect(() => {
        if (!routeData || !routeData.waypoints || routeData.waypoints.length === 0) {
            return;
        }

        const initMap = () => {
            let container: HTMLDivElement | null = null;
            const isDesktop = window.innerWidth >= 1024;
            if (isDesktop) {
                container = mapContainerDesktop.current;
            } else {
                container = mapContainerMobile.current;
            }
            if (container) {
                const rect = container.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) {
                    container = null;
                }
            }
            if (!container) {
                if (initMap.retryCount === undefined) initMap.retryCount = 0;
                initMap.retryCount++;
                if (initMap.retryCount < 10) setTimeout(initMap, 100);
                return;
            }
            if (initMap.retryCount !== undefined) initMap.retryCount = 0;

            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }

            const lats = routeData.waypoints.map(wp => wp.coordinates[1]);
            const lngs = routeData.waypoints.map(wp => wp.coordinates[0]);
            const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
            const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

            const newMap = L.map(container).setView([centerLat, centerLng], 11);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 19,
            }).addTo(newMap);

            const routePoints: L.LatLngExpression[] = [];

            routeData.waypoints.forEach((waypoint, index) => {
            try {
                const position: L.LatLngExpression = [waypoint.coordinates[1], waypoint.coordinates[0]];
                routePoints.push(position);

                let customIcon;
                if (index === 0) {
                    customIcon = L.divIcon({
                        className: 'custom-icon',
                        html: `<div id="planning-marker-0" style="background: linear-gradient(135deg, #8b5cf6, #6366f1); width: 22px; height: 22px; border-radius: 50%;
                                display: flex; align-items: center; justify-content: center; border: 1px solid rgba(255, 255, 255, 0.8);
                                box-shadow: 0 0 4px rgba(139, 92, 246, 0.5), 0 0 8px rgba(139, 92, 246, 0.3);
                                transition: width 0.2s ease, height 0.2s ease, box-shadow 0.2s ease, border 0.2s ease;">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                                stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 0 1px rgba(255,255,255,0.8)); pointer-events: none;">
                                <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
                                <circle cx="12" cy="10" r="3"/>
                                </svg>
                                </div>`,
                        iconSize: [22, 22],
                        iconAnchor: [11, 11],
                    });
                } else {
                    customIcon = L.divIcon({
                        className: 'custom-icon',
                        html: `<div id="planning-marker-${index}" style="background: linear-gradient(135deg, #6366f1, #8b5cf6); width: 20px; height: 20px; border-radius: 50%;
                                display: flex; align-items: center; justify-content: center; color: white; font-weight: 600; font-size: 11px;
                                border: 1px solid rgba(255, 255, 255, 0.8);
                                box-shadow: 0 0 4px rgba(99, 102, 241, 0.5), 0 0 8px rgba(99, 102, 241, 0.3);
                                transition: width 0.2s ease, height 0.2s ease, box-shadow 0.2s ease, border 0.2s ease, font-size 0.2s ease;">
                                ${index}
                                </div>`,
                        iconSize: [20, 20],
                        iconAnchor: [10, 10],
                    });
                }

                const marker = L.marker(position, { icon: customIcon }).addTo(newMap)
                marker.on('click', () => {
                    scrollToIndexRef.current(index)
                })
            } catch (err) {
                console.error(`Erreur lors de l'ajout du marqueur ${index}:`, err);
            }
        });

            if (routePoints.length > 1) {
                L.polyline(routePoints, {
                    color: '#a78bfa',
                    weight: 4,
                    opacity: 0.25,
                    lineJoin: 'round',
                    lineCap: 'round'
                }).addTo(newMap);

                L.polyline(routePoints, {
                    color: '#8b5cf6',
                    weight: 2.5,
                    opacity: 0.85,
                    lineJoin: 'round',
                    lineCap: 'round'
                }).addTo(newMap);

                const localTravelTimes: number[] = [];
                for (let i = 0; i < routeData.waypoints.length - 1; i++) {
                    let dur = 0;
                    if (routeData.route && typeof routeData.route === 'object' && 'legs' in routeData.route) {
                        const legs = routeData.route.legs;
                        if (Array.isArray(legs) && legs[i] && 'duration' in legs[i]) {
                            dur = Math.round(legs[i].duration / 60);
                        }
                    } else {
                        const sp = routeData.waypoints[i].coordinates;
                        const ep = routeData.waypoints[i + 1].coordinates;
                        const dx = sp[1] - ep[1];
                        const dy = sp[0] - ep[0];
                        const distance = Math.sqrt(dx * dx + dy * dy);
                        const ratio = distance / (routeData.totalDistance * 0.01);
                        dur = Math.round((routeData.totalDuration * ratio) / 10);
                    }
                    localTravelTimes.push(dur);
                }

                for (let i = 0; i < routePoints.length - 1; i++) {
                    const startPoint = routePoints[i];
                    const endPoint = routePoints[i + 1];
                    const midLat = (startPoint[0] + endPoint[0]) / 2;
                    const midLng = (startPoint[1] + endPoint[1]) / 2;
                    let estimatedDuration = "";
                    if (i < localTravelTimes.length) estimatedDuration = String(localTravelTimes[i]);
                    if (estimatedDuration !== "") {
                        const timeIcon = L.divIcon({
                            className: 'time-label',
                            html: `<div style="background: linear-gradient(135deg, rgba(139, 92, 246, 0.9), rgba(99, 102, 241, 0.9)); color: white;
                                    font-size: 9px; font-weight: 600; padding: 2px 5px; border-radius: 4px;
                                    border: 1px solid rgba(255, 255, 255, 0.25);
                                    box-shadow: 0 0 4px rgba(139, 92, 246, 0.4), 0 0 8px rgba(139, 92, 246, 0.2);
                                    white-space: nowrap; width: fit-content;
                                    max-width: 50px; overflow: hidden; text-overflow: ellipsis; text-align: center;
                                    text-shadow: 0 0 2px rgba(255, 255, 255, 0.6);">
                                    ${estimatedDuration} min</div>`,
                            iconSize: [0, 0],
                            iconAnchor: [12, 6]
                        });
                        L.marker([midLat, midLng], {
                            icon: timeIcon,
                            interactive: false,
                            zIndexOffset: -1000
                        }).addTo(newMap);
                    }
                }
            }

            if (routePoints.length > 0) {
                const bounds = L.latLngBounds(routePoints);
                newMap.fitBounds(bounds, { paddingTopLeft: [40, 40], paddingBottomRight: [40, 220] });
            }

            mapRef.current = newMap;

            // Add user location marker if position is already known
            if (userLocationRef.current) {
                const { lat, lng } = userLocationRef.current;
                const userIcon = L.divIcon({
                    className: 'user-location-marker',
                    html: `<div style="background-color:#3B82F6;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);animation:userLocPulse 2s infinite;"></div>`,
                    iconSize: [18, 18],
                    iconAnchor: [9, 9]
                });
                userLocationMarkerRef.current = L.marker([lat, lng], { icon: userIcon, zIndexOffset: 1000 }).addTo(newMap);
            }

            requestAnimationFrame(() => highlightMarker(0))
        };

        const timeoutId = setTimeout(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    initMap();
                });
            });
        }, 200);

        return () => {
            clearTimeout(timeoutId);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
            userLocationMarkerRef.current = null;
        };
    }, [routeData]);

    // Track user's real-time location and show it on the map
    useEffect(() => {
        if (!navigator.geolocation) return;

        watchIdRef.current = navigator.geolocation.watchPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                userLocationRef.current = { lat: latitude, lng: longitude };

                if (mapRef.current) {
                    if (userLocationMarkerRef.current) {
                        userLocationMarkerRef.current.setLatLng([latitude, longitude]);
                    } else {
                        const userIcon = L.divIcon({
                            className: 'user-location-marker',
                            html: `<div style="background-color:#3B82F6;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);animation:userLocPulse 2s infinite;"></div>`,
                            iconSize: [18, 18],
                            iconAnchor: [9, 9]
                        });
                        userLocationMarkerRef.current = L.marker([latitude, longitude], {
                            icon: userIcon,
                            zIndexOffset: 1000
                        }).addTo(mapRef.current);
                    }
                }
            },
            (error) => {
                if (error.code === error.TIMEOUT || error.code === error.PERMISSION_DENIED) return;
                console.warn('Geolocation error:', error.message);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
        );

        return () => {
            if (watchIdRef.current !== null && navigator.geolocation) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    const fetchOptimizedRoute = async () => {
      if (!date) {
        setError('Veuillez sélectionner une date')
        return
      }
      const cached = getCachedRoute(date)
      if (cached) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError('')
      try {
        const response = await fetch(API_CONFIG.endpoints.optimizeBookings, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date }),
        });
        const data = await response.json()
        if (data.data?.waypoints) {
            console.log('[TÉLÉPHONES] Numéros reçus du serveur:');
            data.data.waypoints.forEach((wp: any, idx: number) => {
                if (wp.customerName) {
                    console.log(`  ${idx}. ${wp.customerName} - ${wp.phoneNumber || 'NON DISPONIBLE'}`);
                }
            });
        }
        if (!response.ok) {
          throw new Error(data.error || 'Une erreur est survenue')
        }
        setCachedRoute(date, data.data)
        setRouteData(data.data)
      } catch (err) {
        if (!cached) setError(err instanceof Error ? err.message : 'Une erreur est survenue')
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    }

    const LoadingSpinner = () => (
      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    )

    const PhoneIcon = () => (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
      </svg>
    )

    const WazeIcon = () => (
      <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24">
        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12c0 2.9 1.18 5.53 3.1 7.43l-.31 2.57 2.85-1.14A9.93 9.93 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2z"/>
        <circle cx="9" cy="11" r="1.3" fill="rgba(0,0,0,0.45)"/>
        <circle cx="15" cy="11" r="1.3" fill="rgba(0,0,0,0.45)"/>
        <path d="M9.5 14.2c.55.85 1.4 1.3 2.5 1.3s1.95-.45 2.5-1.3" fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="1.1" strokeLinecap="round"/>
      </svg>
    )

    const SmsIcon = () => (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    )

    return (
      <>
        <div className="hidden lg:block w-full max-w-7xl mx-auto p-2 sm:p-4 space-y-4">
          <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/80 backdrop-blur-sm rounded-xl shadow-xl shadow-indigo-500/5 p-4 sm:p-6 border border-indigo-500/20 overflow-hidden">

            {/* ===== DESKTOP LAYOUT ===== */}
            <div className="hidden lg:grid lg:grid-cols-3 lg:gap-4 lg:items-stretch">
              {/* Left column: header + controls + map */}
              <div className="lg:col-span-2 flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400 drop-shadow-[0_0_4px_rgba(34,211,238,0.8)]" />
                  <h2 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-[0_0_8px_rgba(139,92,246,0.6)]">
                    Optimisation des rendez-vous
                  </h2>
                  {routeData && (
                    <button
                      onClick={() => setShowSummary(true)}
                      className="ml-2 flex items-center gap-1.5 text-xs text-purple-300 hover:text-purple-200 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 px-2.5 py-1 rounded-lg transition-all duration-200"
                      title="Résumé de la journée"
                    >
                      <Users className="h-3.5 w-3.5" />
                      <span>Résumé</span>
                    </button>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 min-w-0">
                  <input
                    type="date"
                    value={date}
                    onChange={handleDateChange}
                    className="border border-cyan-500/30 rounded-lg p-2.5 bg-gray-900/60 text-white focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 focus:shadow-lg focus:shadow-cyan-500/30 w-full min-w-0 sm:w-auto backdrop-blur-sm shadow-md transition-all duration-200"
                  />
                  <div className="flex gap-2 sm:gap-4">
                    <button
                      onClick={() => {
                        const today = new Date();
                        setDate(today.toISOString().split('T')[0]);
                        setShouldFetch(true);
                      }}
                      className="flex-1 sm:flex-none bg-gradient-to-r from-cyan-500/20 to-indigo-500/20 hover:from-cyan-500/30 hover:to-indigo-500/30 backdrop-blur-sm text-cyan-200 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm sm:text-base border border-cyan-400/40 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:-translate-y-0.5 flex items-center justify-center"
                    >
                      <Calendar className="h-4 w-4 mr-1.5 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)]" />
                      Aujourd'hui
                    </button>
                    <button
                      onClick={fetchOptimizedRoute}
                      disabled={loading || refreshing}
                      className="flex-1 sm:flex-none bg-gradient-to-r from-indigo-500/20 to-purple-500/20 hover:from-indigo-500/30 hover:to-purple-500/30 disabled:from-gray-600/20 disabled:to-gray-600/20 backdrop-blur-sm text-indigo-200 px-4 py-2.5 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition-all duration-200 text-sm sm:text-base border border-indigo-400/40 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center"
                    >
                      {loading ? (
                        <><LoadingSpinner /><span className="ml-2">Optimisation...</span></>
                      ) : refreshing ? (
                        <><LoadingSpinner /><span className="ml-2">Actualisation...</span></>
                      ) : (
                        <><CheckCircle className="h-4 w-4 mr-1.5 drop-shadow-[0_0_3px_rgba(139,92,246,0.8)]" />Optimiser</>
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-rose-300 p-3 bg-gradient-to-br from-rose-900/40 to-pink-900/40 backdrop-blur-sm rounded-lg text-sm border border-rose-500/50 shadow-lg shadow-rose-500/20 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-rose-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    {error}
                  </div>
                )}

                {routeData && (
                  <>
                    <h3 className="text-base sm:text-lg font-semibold mb-3 bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-[0_0_8px_rgba(139,92,246,0.6)] flex items-center">
                      <MapPin className="h-5 w-5 mr-2 text-cyan-400 drop-shadow-[0_0_4px_rgba(34,211,238,0.8)]" />
                      Visualisation de l'itinéraire
                    </h3>
                    <div
                      ref={mapContainerDesktop}
                      className="w-full h-[600px] rounded-lg overflow-hidden shadow-lg border border-indigo-500/20"
                    />
                  </>
                )}
              </div>

              {/* Right column: waypoint list */}
              {routeData && (
                <div className="lg:col-span-1 flex flex-col gap-4">
                  <h3 className="text-base sm:text-lg font-semibold bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-[0_0_8px_rgba(139,92,246,0.6)] flex items-center flex-shrink-0">
                    <Navigation className="h-5 w-5 mr-2 text-cyan-400 drop-shadow-[0_0_4px_rgba(34,211,238,0.8)]" />
                    Itinéraire optimisé
                  </h3>
                  <div className="overflow-y-auto pr-2 space-y-1 flex-shrink-0" style={{
                    maxHeight: '725px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(99, 102, 241, 0.3) rgba(31, 41, 55, 0.5)'
                  }}>
                    {routeData.waypoints.map((waypoint, index) => (
                      <React.Fragment key={index}>
                        <div className="p-3 border border-indigo-500/20 rounded-lg bg-gradient-to-br from-gray-900/95 to-gray-800/85 backdrop-blur-sm hover:border-indigo-500/40 hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-0.5 transition-all duration-200 shadow-md">
                          {waypoint.type === 'starting_point' ? (
                            <div className="break-words flex items-start">
                              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-gradient-to-br from-indigo-500/30 to-purple-500/30 rounded-full text-white text-xs font-bold mr-2 border border-indigo-400/40 shadow-lg shadow-indigo-500/20">
                                <MapPin className="h-3.5 w-3.5 drop-shadow-[0_0_3px_rgba(139,92,246,0.8)]" />
                              </div>
                              <div className="flex-grow min-w-0">
                                <span className="font-medium text-cyan-300 text-xs drop-shadow-[0_0_3px_rgba(34,211,238,0.6)]">Départ:</span>
                                <a
                                  href={appleMapsUrl(waypoint.address, waypoint.coordinates)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-cyan-400 hover:text-cyan-300 hover:underline ml-1 text-xs block transition-colors drop-shadow-[0_0_3px_rgba(34,211,238,0.6)] break-words cursor-pointer"
                                >
                                  {waypoint.address}
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start">
                              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-gradient-to-br from-indigo-500/30 to-purple-500/30 rounded-full text-white text-xs font-bold mr-2 border border-indigo-400/40 shadow-lg shadow-indigo-500/20">
                                {index}
                              </div>
                              <div className="flex-grow min-w-0">
                                <div className="font-medium text-xs text-white mb-1 drop-shadow-[0_0_3px_rgba(139,92,246,0.6)] break-words">
                                  <NomClient waypoint={waypoint} />
                                </div>
                                {(waypoint.serviceType || waypoint.price !== undefined) && (
                                  <div className="flex items-center gap-1 mb-1">
                                    {waypoint.serviceType && (
                                      <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
                                        {waypoint.serviceType}
                                      </span>
                                    )}
                                    {waypoint.price !== undefined && (
                                      <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                                        {(waypoint.price / 100).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })}
                                      </span>
                                    )}
                                  </div>
                                )}
                                <a
                                  href={appleMapsUrl(waypoint.address, waypoint.coordinates)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-cyan-400 hover:text-cyan-300 hover:underline text-xs break-words block transition-colors drop-shadow-[0_0_3px_rgba(34,211,238,0.6)] cursor-pointer"
                                >
                                  {waypoint.address}
                                </a>
                                {waypoint.phoneNumber && (
                                  <a
                                    href={`tel:${waypoint.phoneNumber}`}
                                    className="text-cyan-400 hover:text-cyan-300 hover:underline text-xs block mt-1 flex items-center transition-colors drop-shadow-[0_0_3px_rgba(34,211,238,0.6)]"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                    </svg>
                                    {waypoint.phoneNumber}
                                  </a>
                                )}
                                {waypoint.startAt && (
                                  <div className="text-gray-300 text-xs mt-1 flex items-center">
                                    <Clock className="h-3 w-3 mr-1 text-cyan-400" />
                                    {new Date(waypoint.startAt).getHours() === 0
                                      ? "Toute la journée"
                                      : new Date(waypoint.startAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                                    }
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {index > 0 && travelTimes[index - 1] !== undefined && (
                          <div className="flex justify-center items-center py-0.5">
                            <div className="flex items-center text-cyan-300 px-2 py-0.5 text-xs bg-gradient-to-br from-gray-900/95 to-gray-800/85 backdrop-blur-sm rounded border border-cyan-500/20 shadow-lg shadow-cyan-500/10 drop-shadow-[0_0_3px_rgba(34,211,238,0.6)]">
                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                              <span>{travelTimes[index - 1]} min</span>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>


          </div>

        </div>

        {/* ===== MOBILE FULL-SCREEN MAP OVERLAY — starts below site header ===== */}
        {createPortal(
          <div className="lg:hidden flex flex-col justify-between" style={{ position: 'fixed', top: 'calc(4rem + env(safe-area-inset-top, 0px))', left: 0, right: 0, bottom: 0, zIndex: 49 }}>

            {/* Map background (only when route data exists) */}
            {routeData && (
              <>
                <div
                  ref={mapContainerMobile}
                  className="absolute inset-0 w-full h-full"
                  style={{ zIndex: 0, isolation: 'isolate' }}
                />
                {loading && (
                  <div className="absolute inset-0" style={{ zIndex: 5, background: 'rgba(8,8,20,0.55)', backdropFilter: 'blur(1px)' }} />
                )}
              </>
            )}

            {/* Top controls bar — always visible */}
            <div className="relative bg-gradient-to-r from-gray-900/95 via-indigo-950/95 to-purple-950/95 backdrop-blur-md border-b border-indigo-500/30 shadow-lg shadow-indigo-500/10 px-3 py-2" style={{ zIndex: 1000 }}>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={date}
                  onChange={handleDateChange}
                  className="flex-1 min-w-0 border border-indigo-500/30 rounded-lg px-3 py-2 bg-transparent text-white text-sm focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-400/60 appearance-none transition-all duration-200"
                />
                {refreshing && (
                  <div className="flex-shrink-0 h-2 w-2 rounded-full bg-cyan-400 animate-pulse" title="Mise à jour…" />
                )}
                {routeData && (
                  <button
                    onClick={() => setShowSummary(true)}
                    className="flex-shrink-0 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 hover:from-purple-500/30 hover:to-indigo-500/30 text-purple-200 p-2 rounded-lg border border-purple-400/40 shadow-lg shadow-purple-500/10 active:scale-95 transition-all duration-200"
                    title="Résumé de la journée"
                  >
                    <Users className="h-4 w-4 drop-shadow-[0_0_3px_rgba(139,92,246,0.8)]" />
                  </button>
                )}
                <button
                  onClick={() => { const today = new Date(); setDate(today.toISOString().split('T')[0]); setShouldFetch(true); }}
                  className="flex-shrink-0 bg-gradient-to-r from-cyan-500/20 to-indigo-500/20 hover:from-cyan-500/30 hover:to-indigo-500/30 text-cyan-200 p-2 rounded-lg border border-cyan-400/40 shadow-lg shadow-cyan-500/10 active:scale-95 transition-all duration-200"
                  title="Aujourd'hui"
                >
                  <Calendar className="h-4 w-4 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)]" />
                </button>
              </div>
            </div>

            {/* Loading state — only when no existing route to display */}
            {loading && !routeData && (
              <div className="flex-1 flex flex-col items-center justify-center gap-3" style={{ zIndex: 1000 }}>
                <div className="animate-spin h-10 w-10 rounded-full border-2 border-indigo-500/30 border-t-indigo-400" />
                <span className="text-sm text-gray-400">Optimisation en cours…</span>
              </div>
            )}

            {/* Error state — only when no route data to display */}
            {!loading && error && !routeData && (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6" style={{ zIndex: 1000 }}>
                <div className="text-rose-300 p-4 bg-gradient-to-br from-rose-900/40 to-pink-900/40 backdrop-blur-sm rounded-xl text-sm border border-rose-500/50 shadow-lg shadow-rose-500/20 text-center">
                  {error}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && !routeData && (
              <div className="flex-1 flex flex-col items-center justify-center gap-2 px-6" style={{ zIndex: 1000 }}>
                <Calendar className="h-12 w-12 text-indigo-400/40" />
                <span className="text-sm text-gray-500 text-center">Aucun rendez-vous trouvé pour cette date</span>
              </div>
            )}

            {/* Bottom carousel stack */}
            {routeData && <div className="relative" style={{ zIndex: 1000, background: 'linear-gradient(to top, rgba(8,8,20,0.95) 0%, rgba(8,8,20,0.80) 60%, transparent 100%)', paddingTop: '2rem', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}>

              {/* Loading overlay replaces cards when re-fetching */}
              {loading && (
                <div className="flex flex-col items-center justify-center gap-3 px-4" style={{ height: '250px' }}>
                  <div className="animate-spin h-9 w-9 rounded-full border-2 border-indigo-500/30 border-t-indigo-400" />
                  <span className="text-sm text-gray-400">Optimisation en cours…</span>
                </div>
              )}

              {/* Stop indicator */}
              <div className="flex justify-between items-center px-4 mb-1.5" style={{ display: loading ? 'none' : undefined }}>
                <span className="text-xs font-semibold text-white/80 bg-gray-900/60 backdrop-blur-sm px-2.5 py-0.5 rounded-full border border-indigo-500/25">
                  {routeData.waypoints[currentCardIndex]?.type === 'starting_point'
                    ? 'Point de départ'
                    : `Arrêt ${currentCardIndex} sur ${routeData.waypoints.length - 1}`}
                </span>
                {routeData.waypoints.length > 1 && (
                  <span className="text-xs text-white/45">
                    Glissez vers la gauche ou la droite &nbsp;{currentCardIndex + 1} / {routeData.waypoints.length}
                  </span>
                )}
              </div>

              {/* Scroll-snap carousel */}
              <div
                ref={carouselRef}
                onScroll={handleCarouselScroll}
                className="flex flex-row flex-nowrap overflow-x-auto overflow-y-hidden"
                style={{
                  scrollSnapType: 'x mandatory',
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-x',
                  scrollbarWidth: 'none',
                  display: loading ? 'none' : undefined,
                }}
              >
                {routeData.waypoints.map((waypoint, index) => {
                  const wazeUrl = `https://waze.com/ul?ll=${waypoint.coordinates[1]},${waypoint.coordinates[0]}&navigate=yes&q=${encodeURIComponent(waypoint.address)}`;
                  const timeStr = waypoint.startAt
                    ? (new Date(waypoint.startAt).getHours() === 0
                        ? 'Toute la journée'
                        : new Date(waypoint.startAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }))
                    : null;

                  return (
                    <div
                      key={index}
                      style={{ flex: '0 0 100%', minWidth: '100%', maxWidth: '100%', scrollSnapAlign: 'start', scrollSnapStop: 'always', boxSizing: 'border-box', padding: '0 0.25rem', height: '250px' }}
                    >
                      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-indigo-500/30 rounded-2xl shadow-2xl shadow-indigo-500/20 p-4 h-full flex flex-col">

                        {waypoint.type === 'starting_point' ? (
                          <>
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-indigo-500/40 to-purple-500/40 rounded-full border border-indigo-400/50 shadow-lg shadow-indigo-500/30">
                                <MapPin className="h-5 w-5 text-cyan-400 drop-shadow-[0_0_3px_rgba(34,211,238,0.8)]" />
                              </div>
                              <div>
                                <span className="text-lg font-bold text-cyan-300 drop-shadow-[0_0_6px_rgba(34,211,238,0.6)]">Point de départ</span>
                                <p className="text-sm text-gray-400 mt-0.5">{waypoint.address}</p>
                              </div>
                            </div>
                            {(() => {
                              const totalMinutes = travelTimes.reduce((a, b) => a + b, 0);
                              const legs = routeData.route && typeof routeData.route === 'object' && 'legs' in routeData.route
                                ? (routeData.route as { legs: RouteLeg[] }).legs : null;
                              const totalKm = legs
                                ? (legs.reduce((s, l) => s + l.distance, 0) / 1000).toFixed(1)
                                : '—';
                              return (
                                <>
                                  <div className="flex items-center gap-2 my-3">
                                    <div className="flex-1 h-px bg-indigo-500/20" />
                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest">Résumé du trajet</span>
                                    <div className="flex-1 h-px bg-indigo-500/20" />
                                  </div>
                                  <div className="flex items-center justify-around">
                                    <div className="flex flex-col items-center gap-0.5">
                                      <span className="text-2xl font-bold text-indigo-300 drop-shadow-[0_0_6px_rgba(139,92,246,0.6)]">{routeData.waypoints.length - 1}</span>
                                      <span className="text-[11px] text-gray-400">arrêts</span>
                                    </div>
                                    <div className="w-px h-10 bg-indigo-500/20" />
                                    <div className="flex flex-col items-center gap-0.5">
                                      <span className="text-2xl font-bold text-cyan-300 drop-shadow-[0_0_6px_rgba(34,211,238,0.6)]">{totalMinutes}</span>
                                      <span className="text-[11px] text-gray-400">minutes</span>
                                    </div>
                                    <div className="w-px h-10 bg-indigo-500/20" />
                                    <div className="flex flex-col items-center gap-0.5">
                                      <span className="text-2xl font-bold text-purple-300 drop-shadow-[0_0_6px_rgba(168,85,247,0.6)]">{totalKm}</span>
                                      <span className="text-[11px] text-gray-400">km</span>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                            {travelTimes[0] !== undefined && (() => {
                              const legs = routeData.route && typeof routeData.route === 'object' && 'legs' in routeData.route
                                ? (routeData.route as { legs: RouteLeg[] }).legs : null;
                              const nextKm = legs && legs[0] ? (legs[0].distance / 1000).toFixed(1) : null;
                              return (
                                <div className="flex items-center gap-2 mt-auto">
                                  <Navigation className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                                  <span className="text-sm text-gray-300">Prochain arrêt</span>
                                  <span className="text-sm font-bold text-indigo-300 ml-auto drop-shadow-[0_0_4px_rgba(139,92,246,0.5)]">{travelTimes[0]} min{nextKm ? ` · ${nextKm} km` : ''}</span>
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            {/* Header: badge + name + travel time */}
                            <div className="flex items-center gap-2.5 mb-1.5">
                              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gradient-to-br from-indigo-500/40 to-purple-500/40 rounded-full text-white text-sm font-bold border border-indigo-400/50 shadow-lg shadow-indigo-500/20">
                                #{index}
                              </div>
                              <NomClient
                                waypoint={waypoint}
                                className="text-base font-bold text-white drop-shadow-[0_0_4px_rgba(139,92,246,0.5)] truncate flex-1 min-w-0"
                              />
                              {index > 0 && travelTimes[index - 1] !== undefined && (
                                <span className="flex-shrink-0 text-[11px] text-cyan-300 bg-gray-900/60 backdrop-blur-sm px-2.5 py-1 rounded-full border border-cyan-500/20 shadow-lg shadow-cyan-500/10 drop-shadow-[0_0_3px_rgba(34,211,238,0.6)]">
                                  {travelTimes[index - 1]} min
                                </span>
                              )}
                            </div>
                            {(waypoint.serviceType || waypoint.price !== undefined) && (
                              <div className="flex items-center gap-1.5 mb-2">
                                {waypoint.serviceType && (
                                  <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
                                    {waypoint.serviceType}
                                  </span>
                                )}
                                {waypoint.price !== undefined && (
                                  <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                                    {(waypoint.price / 100).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Address */}
                            <a href={appleMapsUrl(waypoint.address, waypoint.coordinates)} target="_blank" rel="noopener noreferrer"
                              className="flex items-start gap-1.5 text-sm text-cyan-400 hover:text-cyan-300 mb-2 drop-shadow-[0_0_3px_rgba(34,211,238,0.5)] transition-colors">
                              <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              <span className="truncate">{waypoint.address}</span>
                            </a>

                            {/* Phone */}
                            {waypoint.phoneNumber && (
                              <div className="flex items-center gap-1.5 text-sm text-cyan-300 mb-2 drop-shadow-[0_0_3px_rgba(34,211,238,0.4)]">
                                <PhoneIcon />
                                <span>{waypoint.phoneNumber}</span>
                              </div>
                            )}

                            {/* Time */}
                            {timeStr && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
                                <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                                <span>{timeStr}</span>
                              </div>
                            )}

                            {/* Buttons */}
                            <div className="flex gap-2 mt-auto pt-1">
                              {waypoint.phoneNumber ? (
                                <a
                                  href={`tel:${waypoint.phoneNumber}`}
                                  className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-cyan-600/80 to-indigo-600/80 hover:from-cyan-600 hover:to-indigo-600 text-white text-sm font-semibold py-2.5 rounded-xl border border-cyan-500/40 shadow-lg shadow-cyan-500/20 transition-all active:scale-95"
                                >
                                  <PhoneIcon />
                                  Appeler
                                </a>
                              ) : (
                                <span className="flex-1 flex items-center justify-center gap-1.5 bg-gray-700/50 text-gray-500 text-sm font-semibold py-2.5 rounded-xl border border-gray-600/30 cursor-not-allowed">
                                  <PhoneIcon />
                                  Appeler
                                </span>
                              )}
                              {waypoint.phoneNumber ? (
                                <a
                                  href={`sms:${waypoint.phoneNumber}`}
                                  className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-green-500/80 to-emerald-700/80 hover:from-green-500 hover:to-emerald-700 text-white text-sm font-semibold py-2.5 rounded-xl border border-green-400/40 shadow-lg shadow-green-500/25 drop-shadow-[0_0_6px_rgba(34,197,94,0.4)] transition-all active:scale-95"
                                >
                                  <SmsIcon />
                                  Texto
                                </a>
                              ) : (
                                <span className="flex-1 flex items-center justify-center gap-1.5 bg-gray-700/50 text-gray-500 text-sm font-semibold py-2.5 rounded-xl border border-gray-600/30 cursor-not-allowed">
                                  <SmsIcon />
                                  Texto
                                </span>
                              )}
                              <a
                                href={wazeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-indigo-600/80 to-purple-600/80 hover:from-indigo-600 hover:to-purple-600 text-white text-sm font-semibold py-2.5 rounded-xl border border-indigo-500/40 shadow-lg shadow-indigo-500/20 transition-all active:scale-95"
                              >
                                <WazeIcon />
                                Waze
                              </a>
                            </div>
                          </>
                        )}


                      </div>
                    </div>
                  );
                })}
              </div>

            </div>}
          </div>,
          document.body
        )}

        {/* ===== SUMMARY OVERLAY ===== */}
        {showSummary && routeData && createPortal(
          <div
            className="fixed inset-0 z-[9999] flex flex-col"
            style={{ background: 'rgba(5,5,15,0.85)', backdropFilter: 'blur(8px)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowSummary(false) }}
          >
            <div className="flex flex-col h-full max-w-lg w-full mx-auto" style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px))', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-indigo-500/30">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-400 drop-shadow-[0_0_4px_rgba(139,92,246,0.8)]" />
                  <span className="font-semibold text-white">
                    {routeData.waypoints.filter(w => w.type !== 'starting_point').length} client{routeData.waypoints.filter(w => w.type !== 'starting_point').length > 1 ? 's' : ''} — {new Date(date + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                </div>
                <button
                  onClick={() => setShowSummary(false)}
                  className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all active:scale-90"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Client list */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {routeData.waypoints
                  .filter(w => w.type !== 'starting_point')
                  .map((w, i) => {
                    const timeStr = w.startAt
                      ? (new Date(w.startAt).getHours() === 0
                          ? 'Toute la journée'
                          : new Date(w.startAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }))
                      : null;
                    return (
                      <div key={i} className="bg-gradient-to-br from-gray-900/90 to-gray-800/80 border border-indigo-500/20 rounded-xl p-4 space-y-2.5">
                        {/* Name + time */}
                        <div className="flex items-start justify-between gap-2">
                          <NomClient waypoint={w} className="font-semibold text-white text-base leading-tight" />
                          {timeStr && (
                            <span className="flex-shrink-0 flex items-center gap-1 text-xs text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded-full">
                              <Clock className="h-3 w-3" />
                              {timeStr}
                            </span>
                          )}
                        </div>
                        {/* Service */}
                        {w.serviceType && (
                          <div className="flex items-center gap-2 text-sm text-purple-300">
                            <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-purple-400" />
                            <span>{w.serviceType}</span>
                          </div>
                        )}
                        {/* Address */}
                        <a
                          href={appleMapsUrl(w.address, w.coordinates)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-2 text-sm text-gray-400 hover:text-cyan-300 transition-colors"
                        >
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-gray-500" />
                          <span className="leading-snug">{w.address}</span>
                        </a>
                        {/* Phone */}
                        {w.phoneNumber ? (
                          <a
                            href={`tel:${w.phoneNumber}`}
                            className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            {w.phoneNumber}
                          </a>
                        ) : (
                          <span className="flex items-center gap-2 text-sm text-gray-600">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            Aucun numéro
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    )
}

export default RouteOptimizerSchedule;
